import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { useHistory } from 'react-router'
import { useSelector, useDispatch } from 'react-redux'
import { webSerialDisconnect } from '../../redux/actions'
import {
    MdOutlineArrowBack,
    MdFullscreen,
    MdPlayArrow,
    MdFileUpload,
    MdTerminal,
    MdMonitor,
    MdStop,
    MdWarning,
    MdBuild,
} from 'react-icons/md'
import { BsStars } from 'react-icons/bs'
import { GrPrevious, GrNext } from 'react-icons/gr'
import { RxReset } from 'react-icons/rx'
import '../PlaydynamexNew/PlaydynamexNew.css'
import MakeContext from '../Make/MakeContext'
import MakeHeader from '../ReusableComponents/Header/MakeHeader'
import ContentEngine from '../ContentEngine/ContentEngine'
import {
    parseTemplate,
    templateDefaults,
    extractRegionValues,
} from './microPythonTemplate'
import { useScriptingBridge } from './useScriptingBridge'

const BACKEND_URL = process.env.REACT_APP_API_BASE
const SCRIPTING_URL = process.env.REACT_APP_SCRIPTING_URL || '/scripting'

const BUTTON_ACTIONS = ['run', 'upload', 'connect', 'serial', 'console', 'compile']

function matchNumberCondition(a, c) {
    const v = parseFloat(c.value)
    if (Number.isNaN(v)) return false
    switch (c.op) {
        case '>':
            return a > v
        case '<':
            return a < v
        case '>=':
            return a >= v
        case '<=':
            return a <= v
        case 'range': {
            const v2 = parseFloat(c.value2)
            if (Number.isNaN(v2)) return false
            return a >= Math.min(v, v2) && a <= Math.max(v, v2)
        }
        case '=':
        default:
            return a === v
    }
}

function normalizeExpectation(exp) {
    if (exp == null) return { dataType: 'text', accepted: [] }
    if (typeof exp === 'string')
        return { dataType: 'text', accepted: exp.trim() ? [exp] : [] }
    const dataType = exp.dataType === 'number' ? 'number' : 'text'
    if (Array.isArray(exp.accepted)) return { dataType, accepted: exp.accepted }
    if (dataType === 'number')
        return {
            dataType,
            accepted: [{ op: exp.op || '=', value: exp.value, value2: exp.value2 }],
        }
    return { dataType, accepted: exp.value != null ? [exp.value] : [] }
}

function matchesExpectation(actualRaw, exp) {
    const { dataType, accepted } = normalizeExpectation(exp)
    const actual = String(actualRaw ?? '').trim()

    if (dataType === 'number') {
        const conds = (accepted || []).filter(
            (c) => c && c.value !== '' && c.value != null
        )
        if (conds.length === 0) return true
        const a = parseFloat(actual)
        if (Number.isNaN(a)) return false
        return conds.some((c) => matchNumberCondition(a, c))
    }

    const opts = (accepted || [])
        .map((v) => String(v ?? '').trim())
        .filter((v) => v !== '')
    if (opts.length === 0) return true
    return opts.includes(actual)
}

function stepSatisfied(step, regionValues, satisfiedActions) {
    if (!step) return true
    if (step.expected && typeof step.expected === 'object') {
        const active = new Set(step.activeRegionIds || [])
        for (const [id, expected] of Object.entries(step.expected)) {
            if (!active.has(id)) continue
            
            if (!matchesExpectation(regionValues[id], expected)) return false
        }
    }
    if (step.requiredAction && BUTTON_ACTIONS.includes(step.requiredAction)) {
        if (!satisfiedActions.has(step.requiredAction)) return false
    }
    return true
}

const MicroPythonScreen = ({
    data: dataProp,
    projectId: propProjectId,
    screenId: propScreenId,
    screenType: propScreenType,
    initialStep,
}) => {
    const history = useHistory()

    const makeCtx = useContext(MakeContext)
    const isMake = !!makeCtx
    const { setHeaderProps, setFooterProps, setToolbarProps } = makeCtx || {}
    const isShellMode = isMake && !!setHeaderProps

    const urlParams = useMemo(
        () =>
            new URLSearchParams(
                history.location?.search || window.location.search
            ),
        [history.location?.search]
    )
    const isPreviewMode = urlParams.get('preview') === 'true'
    const PROJECT_ID = propProjectId || urlParams.get('projectId') || 'untitled'
    const screenId = propScreenId || urlParams.get('screenId') || null
    const screenType =
        propScreenType ||
        dataProp?.screenType ||
        dataProp?.type ||
        urlParams.get('type') ||
        'micropython'

    const [transcript, setTranscript] = useState(dataProp || null)
    const [loading, setLoading] = useState(!dataProp)

    const audioRef = useRef(null)
    const iframeRef = useRef(null)
    const appContainerRef = useRef(null)
    const readyRef = useRef(false)

    const [aiVoiceover, setAiVoiceover] = useState(
        () => sessionStorage.getItem('aiVoiceToggle') !== 'false'
    )
    const [typedText, setTypedText] = useState('')
    const webSerialConnected = useSelector(
        (s) => !!(s && s.webSerial && s.webSerial.isConnected)
    )
    const webSerialPort = useSelector((s) => s && s.webSerial && s.webSerial.port)
    const plodeWebWorker = useSelector((s) => s && s.worker)
    const dispatch = useDispatch()
    const [iframeConnected, setIframeConnected] = useState(false)
    const [isProgramRunning, setIsProgramRunning] = useState(false)
  
    const [handingOff, setHandingOff] = useState(false)
  
    const isCScreen = screenType === 'c'
    const deviceConnected = isCScreen
        ? iframeConnected
        : webSerialConnected || iframeConnected || handingOff

    const [firmwareState, setFirmwareState] = useState('unknown')

    useEffect(() => {
        if (dataProp) {
            setTranscript(dataProp)
            setLoading(false)
            return
        }
        const load = async () => {
            try {
                let endpoint = isPreviewMode
                    ? `${BACKEND_URL}/api/staging/${screenType}/${PROJECT_ID}`
                    : `${BACKEND_URL}/api/${screenType}/${PROJECT_ID}`
                if (screenId) endpoint += `/${screenId}`
                const res = isPreviewMode
                    ? await fetch(endpoint, { credentials: 'include' })
                    : await fetch(endpoint)
                if (!res.ok) throw new Error('Failed to fetch coding screen')
                setTranscript(await res.json())
            } catch (err) {
                console.error('Coding screen load error:', err)
            } finally {
                setLoading(false)
            }
        }
        load()
    }, [dataProp, isPreviewMode, PROJECT_ID, screenId, screenType])

    const language =
        transcript?.language || (screenType === 'c' ? 'c' : 'python')
    const isCLang = language === 'c'
    const device = transcript?.device || 'peeCee'
    const deviceVersion = transcript?.deviceVersion || '1.0.0'
    const template = transcript?.template || ''
    const steps = useMemo(
        () => (Array.isArray(transcript?.steps) ? transcript.steps : []),
        [transcript]
    )
    const totalSteps = steps.length

    const storageKey = `micropython_step_${PROJECT_ID}_${screenId || 'default'}`
    const [currentStep, setCurrentStep] = useState(() => {
        if (isPreviewMode) return 1
        if (initialStep) return initialStep
        const saved = parseInt(sessionStorage.getItem(storageKey), 10)
        return !isNaN(saved) && saved > 0 ? saved : 1
    })
    useEffect(() => {
        sessionStorage.setItem(storageKey, String(currentStep))
    }, [currentStep, storageKey])

    const step = steps[currentStep - 1] || null

    const isConnectStep =
        (Array.isArray(step?.enabledButtons) &&
            step.enabledButtons.includes('connect')) ||
        step?.requiredAction === 'connect'

    const showFirmwareWarning = isConnectStep && firmwareState === 'missing'

    useEffect(() => {
        const full = step?.instruction || ''
        setTypedText('')
        if (!full) return
        let i = 0
        const id = setInterval(() => {
            i += 1
            setTypedText(full.slice(0, i))
            if (i >= full.length) clearInterval(id)
        }, 18)
        return () => clearInterval(id)
    }, [step?.instruction])

    const [regionValues, setRegionValues] = useState(() =>
        templateDefaults(template)
    )
    const [currentCode, setCurrentCode] = useState('')

    useEffect(() => {
        setRegionValues(templateDefaults(template))
    }, [template])

    const [satisfiedActions, setSatisfiedActions] = useState(() => new Set())
    useEffect(() => {
        setSatisfiedActions(deviceConnected ? new Set(['connect']) : new Set())
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStep])
    useEffect(() => {
        if (deviceConnected)
            setSatisfiedActions((prev) => new Set(prev).add('connect'))
    }, [deviceConnected])

    const isConnectRequired = step?.requiredAction === 'connect'
    const canAdvance =
        stepSatisfied(step, regionValues, satisfiedActions, currentCode) &&
        (!isConnectRequired || firmwareState === 'ok') &&
        firmwareState !== 'checking'

    const playAudioForStep = useCallback(
        (stepNumber) => {
            if (!aiVoiceover) return
            const base = transcript?.meta?.audioBaseUrl
            if (!base) return
            const url = `${base}${stepNumber}.mp3?t=${Date.now()}`
            if (makeCtx?.playAudio) {
                makeCtx.playAudio(url)
                return
            }
            if (!audioRef.current) return
            audioRef.current.src = url
            audioRef.current
                .play()
                .catch((e) => console.log('Audio playback skipped:', e))
        },
        [transcript, makeCtx, aiVoiceover]
    )

    const applyStep = useCallback(
        (bridge, stepIndex, values) => {
            const s = steps[stepIndex - 1]
            if (!s) return
            const { regions } = parseTemplate(template, values)
            const activeIds = Array.isArray(s.activeRegionIds)
                ? s.activeRegionIds
                : []
            const activeRegions = regions
                .filter((r) => activeIds.includes(r.id))
                .map((r) => ({ ...r, hint: s.hints?.[r.id] }))
            bridge.setEditableRegions(activeRegions)
            bridge.setAnnotations(
                Array.isArray(s.annotations) ? s.annotations : []
            )
        },
        [steps, template]
    )

    const bridge = useScriptingBridge(iframeRef, {
        onReady: () => {
            readyRef.current = true
            const defaults = templateDefaults(template)
            const { code } = parseTemplate(template, defaults)
            bridge.init({
                language,
                device,
                version: deviceVersion,
                projectName: 'Tutorial',
                code,
            })
            applyStep(bridge, currentStep, defaults)
            playAudioForStep(currentStep)
        },
        onRegionEdited: () => {
            
        },
        onCodeChanged: ({ code }) => {
            setCurrentCode(code)
            
            const vals = extractRegionValues(template, code)
            if (vals) setRegionValues((prev) => ({ ...prev, ...vals }))
        },
        onActionClicked: ({ action }) => {
            if (action !== 'connect')
                setSatisfiedActions((prev) => new Set(prev).add(action))
        },
        onActionResult: ({ action, success }) => {
            if (action === 'connect') {
                setIframeConnected(success)
                if (!success) setFirmwareState('unknown')
            }
            if (action === 'checkFirmware') {
                setFirmwareState(success ? 'ok' : 'missing')
            }
            if (action === 'run' && success) setIsProgramRunning(true)
            if (action === 'stop') setIsProgramRunning(false)
            if (success && action !== 'stop' && action !== 'checkFirmware')
                setSatisfiedActions((prev) => new Set(prev).add(action))
        },
    })

    useEffect(() => {
        if (!readyRef.current) return
        applyStep(bridge, currentStep, regionValues)
        playAudioForStep(currentStep)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStep])

    useEffect(() => {
        
        if (isCScreen) return
        if (!webSerialConnected || !webSerialPort) return
        let cancelled = false
        setHandingOff(true)
        ;(async () => {
            try {
                plodeWebWorker?.postMessage?.({ type: 'disconnected' })
            } catch (_) {}
            await new Promise((r) => setTimeout(r, 400))
            if (cancelled) return
            try { await webSerialPort.close?.() } catch {}
            if (cancelled) return
            dispatch(webSerialDisconnect())
            setTimeout(() => { if (!cancelled) bridge.triggerAction('connect') }, 100)
        })()
        const fallback = setTimeout(() => setHandingOff(false), 15000)
        return () => {
            cancelled = true
            clearTimeout(fallback)
        }
    }, [webSerialConnected, webSerialPort, plodeWebWorker, dispatch, bridge])

    useEffect(() => {
        if (iframeConnected) setHandingOff(false)
    }, [iframeConnected])

    // When the USB cable is physically removed, the browser fires a disconnect
    // event on navigator.serial. The iframe won't send ACTION_RESULT in this
    // case, so iframeConnected would stay true and the green icon would persist.
    useEffect(() => {
        const handlePhysicalDisconnect = () => {
            setIframeConnected(false)
            setHandingOff(false)
        }
        navigator.serial?.addEventListener('disconnect', handlePhysicalDisconnect)
        return () =>
            navigator.serial?.removeEventListener('disconnect', handlePhysicalDisconnect)
    }, [])

    useEffect(() => {
        if (!isConnectStep) return
        if (!deviceConnected) {
            setFirmwareState('unknown')
            return
        }
        setFirmwareState('checking')
        const id = setTimeout(() => bridge.triggerAction('checkFirmware'), 900)
        return () => clearTimeout(id)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStep, isConnectStep, deviceConnected])

    useEffect(() => {
        if (firmwareState !== 'checking') return
        const id = setTimeout(() => {
            setFirmwareState((s) => (s === 'checking' ? 'missing' : s))
        }, 10000)
        return () => clearTimeout(id)
    }, [firmwareState])

    const handleNext = useCallback(() => {
        if (!canAdvance) return
        const globalStep = ContentEngine.getCurrentScreenOffset() + currentStep
        ContentEngine.tryEarnStep(globalStep)
        if (currentStep < totalSteps) {
            setCurrentStep((s) => s + 1)
        } else {
            if (isPreviewMode) return
            sessionStorage.removeItem(storageKey)
            if (dataProp) {
                ContentEngine.next()
            }
        }
    }, [canAdvance, currentStep, totalSteps, isPreviewMode, storageKey, dataProp])

    const handlePrev = useCallback(() => {
        if (currentStep > 1) {
            setCurrentStep((s) => s - 1)
        } else {
            sessionStorage.removeItem(storageKey)
            if (dataProp) ContentEngine.prev()
        }
    }, [currentStep, storageKey, dataProp])

    const goBack = useCallback(() => {
        if (dataProp) ContentEngine.prevToStart()
        else history.goBack()
    }, [history, dataProp])

    const onStepClick = useCallback(
        (stepNum) => {
            ContentEngine.breakSequentialChain()
            setCurrentStep(stepNum)
        },
        []
    )

    const handleFullscreen = useCallback(() => {
        const el = appContainerRef.current
        if (!document.fullscreenElement) el?.requestFullscreen?.()
        else document.exitFullscreen?.()
    }, [])

    const resetTutorial = useCallback(() => {
        setCurrentStep(1)
        setRegionValues(templateDefaults(template))
    }, [template])

    const toggleAiVoiceover = useCallback(() => {
        setAiVoiceover((prev) => {
            const next = !prev
            sessionStorage.setItem('aiVoiceToggle', String(next))
            if (!next && makeCtx?.stopAudio) makeCtx.stopAudio()
            return next
        })
    }, [makeCtx])

    const checkingLabel = isCLang
        ? 'Checking device & agent…'
        : 'Checking MicroPython firmware…'
    const nextTooltip =
        firmwareState === 'checking'
            ? checkingLabel
            : !stepSatisfied(step, regionValues, satisfiedActions, currentCode)
              ? 'Complete this step to continue'
              : (isConnectRequired && firmwareState !== 'ok')
                ? 'Connect your device to continue'
                : 'Complete this step to continue'

    const triggerCodingAction = useCallback(
        (action) => {
            bridge.triggerAction(action)
            if (action !== 'connect')
                setSatisfiedActions((prev) => new Set(prev).add(action))
        },
        [bridge]
    )

 
    const headerConnectOverride = useCallback(() => {
        bridge.triggerAction('connect')
    }, [bridge])

    const handleStop = useCallback(() => {
        setIsProgramRunning(false) // optimistic
        bridge.triggerAction('stop')
    }, [bridge])

    const CODING_ACTION_DEFS = useMemo(
        () => [
            
            { key: 'compile', title: 'Compile', icon: <MdBuild /> },
            { key: 'run', title: 'Run', icon: <MdPlayArrow /> },
            { key: 'upload', title: 'Upload', icon: <MdFileUpload /> },
        ],
        []
    )
    const codingActions = useMemo(() => {
        const enabled = step?.enabledButtons || []
        return CODING_ACTION_DEFS.filter((d) => enabled.includes(d.key)).map(
            (d) => ({
                ...d,
                highlight: !isProgramRunning && step?.requiredAction === d.key && !satisfiedActions.has(d.key),
                disabled: isProgramRunning,
                onClick: () => triggerCodingAction(d.key),
            })
        )
    }, [step, CODING_ACTION_DEFS, triggerCodingAction, isProgramRunning, satisfiedActions])

    const viewToggles = useMemo(() => {
        const enabled = step?.enabledButtons || []
        return [
            { key: 'console', title: 'Console', icon: <MdTerminal /> },
            { key: 'serial', title: 'Serial monitor', icon: <MdMonitor /> },
        ]
            .filter((v) => enabled.length === 0 || enabled.includes(v.key))
            .map((v) => ({
                ...v,
                highlight: step?.requiredAction === v.key && !satisfiedActions.has(v.key),
                onClick: () => {
                    bridge.triggerAction(v.key)
                    // Satisfy the required action so Next can unlock.
                    setSatisfiedActions((prev) => new Set(prev).add(v.key))
                },
            }))
    }, [bridge, step, satisfiedActions])

    // Stop button: appears only while program is running; sits first so it's
    // immediately visible. Disappears automatically when the program ends or
    // the user clicks it.
    const stopButton = useMemo(
        () =>
            isProgramRunning
                ? [
                      {
                          key: 'stop',
                          title: 'Stop',
                          icon: <MdStop />,
                          isStop: true,
                          onClick: handleStop,
                      },
                  ]
                : [],
        [isProgramRunning, handleStop]
    )

    const toolbarButtons = useMemo(
        () => [...stopButton, ...codingActions, ...viewToggles],
        [stopButton, codingActions, viewToggles]
    )

    useEffect(() => {
        if (!setHeaderProps) return
        setHeaderProps({
            title: transcript?.meta?.title || transcript?.title || 'Coding',
            showJumpTo: false,
            titlePosition: 'left',
            help_btnPosition: 'right',
            isGoBack: true,
            goBack,
            showHelp: true,
            showBluetoothBtn: true,
            showUsbBtn: true,
            useUsbIcon: true,
            bluetoothConnectedOverride: deviceConnected,
            connectOverride: isCScreen ? headerConnectOverride : undefined,
            currentStep,
            totalSteps,
            stepTitles: steps.map((s) => s?.title || ''),
            onStepClick,
        })
    }, [
        setHeaderProps,
        transcript,
        currentStep,
        totalSteps,
        steps,
        goBack,
        onStepClick,
        deviceConnected,
        isCScreen,
        headerConnectOverride,
    ])

    useEffect(() => {
        if (!setFooterProps) return
        setFooterProps({
            stepCounter: `Step ${currentStep} of ${totalSteps || 1}`,
            stepTitle: step?.title || '',
            prevDisabled: dataProp ? false : currentStep === 1,
            nextDisabled: !canAdvance,
            showNextTooltip: !canAdvance,
            nextTooltipText: nextTooltip,
            nextLabel: currentStep === totalSteps ? 'Continue' : 'Next',
            onPrev: handlePrev,
            onNext: handleNext,
        })
    }, [
        setFooterProps,
        currentStep,
        totalSteps,
        step?.title,
        canAdvance,
        nextTooltip,
        handlePrev,
        handleNext,
        dataProp,
    ])

    useEffect(() => {
        if (!setToolbarProps) return
        setToolbarProps({
            customButtons: toolbarButtons.map((a) => ({
                key: a.key,
                title: a.title,
                icon: a.icon,
                highlight: a.highlight,
                active: a.active,
                isStop: a.isStop,
                onClick: a.onClick,
            })),
            aiVoiceover,
            onAiToggle: toggleAiVoiceover,
            onFullscreen: handleFullscreen,
            onReset: resetTutorial,
        })
    }, [
        setToolbarProps,
        toolbarButtons,
        aiVoiceover,
        toggleAiVoiceover,
        handleFullscreen,
        resetTutorial,
    ])

    const iframeSrc = `${SCRIPTING_URL}?embed=1&language=${encodeURIComponent(
        language
    )}&device=${encodeURIComponent(device)}&version=${encodeURIComponent(
        deviceVersion
    )}`

    if (loading)
        return <div className="pdx-app-container">Loading Project...</div>
    if (!transcript)
        return <div className="pdx-app-container">Error loading screen data.</div>

    const fwCopy = isCLang
        ? {
              title: 'Device Not Ready for C',
              body: 'C coding needs the Plode Web Agent running and a device with standard (non-MicroPython) firmware.',
              stepsTitle: 'To use C:',
              steps: [
                  'Install and run the Plode Web Agent.',
                  'If the device has MicroPython, flash standard firmware (hold BOOT, reconnect, release).',
                  'Click "Fix Setup" below.',
              ],
              primaryLabel: 'Fix Setup',
          }
        : {
              title: 'MicroPython Firmware Not Detected',
              body: 'Your device is not running MicroPython firmware. You need to flash MicroPython before you can use this coding environment.',
              stepsTitle: 'To flash MicroPython firmware:',
              steps: [
                  'Disconnect the device.',
                  'Hold the BOOT button, reconnect via USB, then release.',
                  'Click "Flash MicroPython" below.',
              ],
              primaryLabel: 'Flash MicroPython',
          }

    const firmwareWarningOverlay = showFirmwareWarning && (
        <div
            style={{
                position: 'absolute',
                inset: 0,
                zIndex: 200,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(15, 23, 42, 0.72)',
                backdropFilter: 'blur(6px)',
                WebkitBackdropFilter: 'blur(6px)',
            }}
        >
            <div
                style={{
                    background: '#fff',
                    borderRadius: 16,
                    padding: '36px 40px',
                    maxWidth: 460,
                    width: 'calc(100% - 48px)',
                    boxShadow: '0 24px 64px rgba(0,0,0,0.28)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 16,
                    textAlign: 'center',
                }}
            >
                <div
                    style={{
                        width: 64,
                        height: 64,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #FFF3E0, #FFE0B2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}
                >
                    <MdWarning size={34} color="#E65100" />
                </div>

                <h2
                    style={{
                        margin: 0,
                        fontSize: '1.25rem',
                        fontWeight: 700,
                        color: '#0f172a',
                        fontFamily: 'Halcyon Regular, sans-serif',
                    }}
                >
                    {fwCopy.title}
                </h2>

                <p
                    style={{
                        margin: 0,
                        fontSize: '0.9rem',
                        color: '#475569',
                        lineHeight: 1.6,
                    }}
                >
                    {fwCopy.body}
                </p>

                <div
                    style={{
                        background: '#FFF8E1',
                        border: '1px solid #FFE082',
                        borderRadius: 10,
                        padding: '12px 16px',
                        fontSize: '0.82rem',
                        color: '#5D4037',
                        lineHeight: 1.6,
                        textAlign: 'left',
                        width: '100%',
                        boxSizing: 'border-box',
                    }}
                >
                    <strong>{fwCopy.stepsTitle}</strong>
                    <ol style={{ margin: '6px 0 0', paddingLeft: 20 }}>
                        {fwCopy.steps.map((s, i) => (
                            <li key={i}>{s}</li>
                        ))}
                    </ol>
                </div>

                <div
                    style={{
                        display: 'flex',
                        gap: 12,
                        width: '100%',
                        marginTop: 4,
                    }}
                >
                    <button
                        onClick={() => {
                            if (!isCLang)
                                localStorage.setItem('microPythonUpdateConsent', 'true')
                            history.push('/deviceSelection')
                        }}
                        style={{
                            flex: 1,
                            padding: '12px 0',
                            background: 'linear-gradient(135deg, #FF6F00, #E65100)',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 10,
                            fontWeight: 700,
                            fontSize: '0.9rem',
                            cursor: 'pointer',
                            fontFamily: 'Halcyon Regular, sans-serif',
                            letterSpacing: '0.02em',
                        }}
                    >
                        {fwCopy.primaryLabel}
                    </button>
                    <button
                        onClick={() => {
                            setFirmwareState('checking')
                            bridge.triggerAction('checkFirmware')
                        }}
                        style={{
                            flex: 1,
                            padding: '12px 0',
                            background: '#f1f5f9',
                            color: '#475569',
                            border: '1px solid #e2e8f0',
                            borderRadius: 10,
                            fontWeight: 600,
                            fontSize: '0.9rem',
                            cursor: 'pointer',
                            fontFamily: 'Halcyon Regular, sans-serif',
                        }}
                    >
                        Check Again
                    </button>
                </div>
            </div>
        </div>
    )

    const firmwareCheckingBanner = firmwareState === 'checking' && (
        <div
            style={{
                position: 'absolute',
                top: 16,
                right: 16,
                zIndex: 200,
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                background: 'rgba(15,23,42,0.9)',
                color: '#fff',
                padding: '10px 16px',
                borderRadius: 10,
                fontSize: '0.82rem',
                fontFamily: 'Halcyon Regular, sans-serif',
                boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
            }}
        >
            <span
                style={{
                    width: 14,
                    height: 14,
                    border: '2px solid rgba(255,255,255,0.35)',
                    borderTopColor: '#fff',
                    borderRadius: '50%',
                    display: 'inline-block',
                    animation: 'mpSpin 0.7s linear infinite',
                }}
            />
            {checkingLabel}
            <style>{`@keyframes mpSpin{to{transform:rotate(360deg)}}`}</style>
        </div>
    )

    return (
        <div className="pdx-app-container" ref={appContainerRef}>
            {!isShellMode && (
                <div className="pdx-header">
                    <MakeHeader
                        showTimeline={isMake || isPreviewMode}
                        title={transcript?.meta?.title || transcript?.title}
                        showJumpTo={false}
                        titlePosition="left"
                        help_btnPosition="right"
                        isGoBack={true}
                        goBack={goBack}
                        showHelp={true}
                        showBluetoothBtn={true}
                        showUsbBtn={true}
                        useUsbIcon={true}
                        bluetoothConnectedOverride={deviceConnected}
                        connectOverride={
                            isCScreen ? headerConnectOverride : undefined
                        }
                        currentStep={currentStep}
                        totalSteps={totalSteps}
                        stepTitles={steps.map((s) => s?.title || '')}
                        onStepClick={onStepClick}
                    />
                </div>
            )}

            <main
                className="pdx-viewer-container"
                style={{ position: 'relative' }}
            >
                {firmwareWarningOverlay}
                {firmwareCheckingBanner}
                {step?.instruction && (
                    <div
                        style={{
                            position: 'absolute',
                            top: '10px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            maxWidth: '600px',
                            width: 'calc(100% - 40px)',
                            padding: '12px 24px',
                            borderRadius: '20px',
                            background: 'rgba(255, 255, 255, 0.75)',
                            backdropFilter: 'blur(10px)',
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                            border: '1px solid rgba(0, 0, 0, 0.08)',
                            zIndex: 101,
                        }}
                    >
                        <div
                            style={{
                                fontSize: '1.1rem',
                                color: '#333',
                                wordBreak: 'break-word',
                                whiteSpace: 'pre-wrap',
                                fontFamily: 'Halcyon Regular, sans-serif',
                                lineHeight: '1.5',
                                textAlign: 'center',
                                width: '100%',
                            }}
                        >
                            {typedText}
                        </div>

                        {/* Explanation highlights — shown below the instruction text
                            so the learner can read what each highlighted code does */}
                        {Array.isArray(step.annotations) &&
                            step.annotations.filter((a) => a?.note).length >
                                0 && (
                                <ul
                                    style={{
                                        margin: '10px 0 0',
                                        padding: '8px 12px',
                                        listStyle: 'none',
                                        borderTop:
                                            '1px solid rgba(59,130,246,0.2)',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 6,
                                    }}
                                >
                                    {step.annotations
                                        .filter((a) => a?.note)
                                        .map((a, idx) => (
                                            <li
                                                key={idx}
                                                style={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: 3,
                                                    fontSize: 13,
                                                    color: '#1e3a8a',
                                                    lineHeight: 1.45,
                                                }}
                                            >
                                                <code
                                                    style={{
                                                        display: 'block',
                                                        background: '#0b1021',
                                                        color: '#93c5fd',
                                                        padding: '3px 8px',
                                                        borderRadius: 4,
                                                        fontSize: 12,
                                                        whiteSpace: 'pre',
                                                        wordBreak: 'break-all',
                                                        marginBottom: 2,
                                                    }}
                                                >
                                                    {a.text ||
                                                        `Line ${a.startLineNumber}`}
                                                </code>
                                                <span style={{ color: '#374151' }}>
                                                    {a.note}
                                                </span>
                                            </li>
                                        ))}
                                </ul>
                            )}
                    </div>
                )}

                <iframe
                    ref={iframeRef}
                    src={iframeSrc}
                    title="MicroPython editor"
                    allow="serial; usb"
                    style={{
                        width: '100%',
                        height: '100%',
                        border: 'none',
                        display: 'block',
                    }}
                />

                {!isShellMode && (
                    <div className="toolbar-sidebar">
                        {toolbarButtons.map((a) => (
                            <button
                                key={a.key}
                                className={`toolbar-button ${a.active ? 'active' : ''} ${a.highlight ? 'mp-action-highlight' : ''}`}
                                style={a.isStop ? { background: '#ef4444', color: '#fff', borderRadius: '50%' } : undefined}
                                title={a.title}
                                onClick={a.onClick}
                                disabled={a.disabled}
                            >
                                {a.icon}
                            </button>
                        ))}
                        <button
                            className={`toolbar-button ${aiVoiceover ? 'active' : ''}`}
                            title="AI Voiceover"
                            onClick={toggleAiVoiceover}
                        >
                            <BsStars />
                        </button>
                        <button
                            className="toolbar-button"
                            title="Fullscreen"
                            onClick={handleFullscreen}
                        >
                            <MdFullscreen />
                        </button>
                        <button
                            className="toolbar-button"
                            title="Reset"
                            onClick={resetTutorial}
                        >
                            <RxReset />
                        </button>
                    </div>
                )}

                <audio ref={audioRef} />
            </main>

            {!isShellMode && (
                <footer
                    style={{
                        gridRow: 3,
                        display: 'flex',
                        alignItems: 'center',
                        height: 70,
                        padding: '0 20px',
                        borderTop: '1px solid #e0e0e0',
                        backgroundColor: 'white',
                        boxSizing: 'border-box',
                        position: 'relative',
                        flexShrink: 0,
                    }}
                >
                    <div className="step-counter" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        {isPreviewMode && (
                            <button
                                onClick={() => history.goBack()}
                                style={{
                                    background: '#ef4444',
                                    color: 'white',
                                    border: 'none',
                                    padding: '8px 16px',
                                    borderRadius: 8,
                                    fontWeight: 'bold',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    fontSize: 13,
                                    lineHeight: 1,
                                }}
                            >
                                <MdOutlineArrowBack size={16} style={{ display: 'block' }} />
                                Exit Preview
                            </button>
                        )}
                        Step {currentStep} of {totalSteps || 1}
                    </div>
                    <div
                        className="step-title"
                        style={{
                            position: 'absolute',
                            left: '50%',
                            top: '50%',
                            transform: 'translateX(-50%) translateY(-50%)',
                            textAlign: 'center',
                            pointerEvents: 'none',
                        }}
                    >
                        {step?.title}
                    </div>
                    <div className="nav-buttons">
                        <button
                            className="nav-btn"
                            onClick={handlePrev}
                            disabled={dataProp ? false : currentStep === 1}
                        >
                            <GrPrevious />
                            <span className="footer-btn-text">Previous</span>
                        </button>
                        <button
                            className="nav-btn"
                            onClick={handleNext}
                            disabled={
                                !canAdvance ||
                                (isPreviewMode && currentStep === totalSteps)
                            }
                            title={
                                isPreviewMode && currentStep === totalSteps
                                    ? 'End of preview — use Exit Preview. In a real course this goes to the next screen.'
                                    : !canAdvance
                                      ? nextTooltip
                                      : undefined
                            }
                        >
                            <span className="footer-btn-text">
                                {currentStep === totalSteps ? 'Continue' : 'Next'}
                            </span>
                            <GrNext />
                        </button>
                    </div>
                </footer>
            )}
        </div>
    )
}

export default MicroPythonScreen
